import React, { useMemo } from 'react';

import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { ErrorBoundary } from '../../../App/ErrorBoundary';
import { Reorder } from '../../../core/api/order/order.types';
import { Basket } from '../../../core/state/order/order.types';
import { newReorder } from '../../../utils/orderUtils';
import { urlUtils } from '../../../utils/urlUtils';
import { ToCurrency } from '../../../weborder/BasketControl';
import { HorizontalList } from '../../layouts/HorizontalList';
import { ButtonCard } from '../../molecules/ButtonCard';
import { ReorderCard } from '../../molecules/ReorderCard';
import { ReorderSkeleton } from './components/ReorderSkeleton';

const useStyles = makeStyles((theme: Theme) => ({
  reorderCard: {
    display: 'flex',
    height: 126,
    marginRight: theme.spacing(1.25),
    maxWidth: 280,
    minWidth: 280, // "width: 280" is not working here
  },
  reorderCardSingle: {
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    reorderCardSingle: { width: 280 },
  },
}));

type Props = {
  deliveryLocationId?: string;
  isLoading?: boolean;
  languageCode: string;
  listTitle: string;
  orders: Reorder[];
  setBasket: (payload: Basket) => void;
  setUnavailableReorderItems: (items: string[]) => void;
  source: 'Store list' | 'Menu';
  translate: TranslateFunction;
};

export function ReorderList(props: Props): JSX.Element {
  const {
    deliveryLocationId,
    isLoading,
    languageCode,
    listTitle,
    orders,
    setBasket,
    setUnavailableReorderItems,
    source,
    translate,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme?.breakpoints.down('sm'));

  const navigateToPreviousOrdersScreen = (): void => {
    urlUtils.navigateToHash('/history');
  };

  const handleReorder = (order: Reorder): void => {
    newReorder(
      order,
      setBasket,
      setUnavailableReorderItems,
      source,
      deliveryLocationId,
    );
  };

  const listItems = useMemo(() => {
    const reorderCardStyles = clsx(
      classes.reorderCard,
      orders?.length === 1 && classes.reorderCardSingle,
    );
    const items = orders?.map((order) => {
      const description = order.MenuItems?.map((x) => x.Name).join(', ');

      return (
        <div className={reorderCardStyles} key={order.OrderId}>
          <ReorderCard
            action={(): void => handleReorder(order)}
            actionText={translate('ReorderCard_Reorder') as string}
            description={description}
            price={ToCurrency(
              order.TotalAmount,
              order.Currency.Id,
              languageCode,
            )}
            title={order.Store.Name}
          />
        </div>
      );
    });

    if (isMobileScreen) {
      items.push(
        <div className={classes.reorderCard} key="all-previous-card">
          <ButtonCard
            onClick={navigateToPreviousOrdersScreen}
            title={translate('Reorder_AllPreviousOrders') as string}
          />
        </div>,
      );
    }

    return items;
  }, [isMobileScreen, orders]);

  const actionText = !isMobileScreen
    ? (translate('ReorderCard_ShowAll') as string)
    : undefined;

  if (isLoading) {
    return <ReorderSkeleton />;
  }

  return (
    <ErrorBoundary identifier="reorder-list">
      <HorizontalList
        action={navigateToPreviousOrdersScreen}
        actionText={actionText}
        dataFd="reorder-list"
        listItems={listItems}
        listName="reorder-list"
        title={listTitle}
      />
    </ErrorBoundary>
  );
}
